import {
  Article,
  ArticleFormData,
  ArticleCategory,
  ArticleTag,
  ArticleSearchParams,
} from '../models/article';
import { API_URL } from './common';

export interface ArticleService {
  // 記事関連
  getArticles(params?: ArticleSearchParams): Promise<{
    articles: Article[];
    total: number;
    page: number;
    limit: number;
  }>;
  getArticle(id: number): Promise<Article>;
  createArticle(formData: ArticleFormData): Promise<Article>;
  updateArticle(id: number, formData: ArticleFormData): Promise<Article>;
  deleteArticle(id: number): Promise<void>;

  // カテゴリ関連
  getCategories(): Promise<ArticleCategory[]>;
  createCategory(
    category: Omit<ArticleCategory, 'id'>
  ): Promise<ArticleCategory>;
  updateCategory(
    id: number,
    category: Partial<ArticleCategory>
  ): Promise<ArticleCategory>;
  deleteCategory(id: number): Promise<void>;

  // タグ関連
  getTags(): Promise<ArticleTag[]>;
  createTag(tag: Omit<ArticleTag, 'id' | 'count'>): Promise<ArticleTag>;
  deleteTag(id: number): Promise<void>;

  // 画像アップロード
  uploadImage(file: File): Promise<{ url: string }>;
}

class ArticleServiceImpl implements ArticleService {
  private baseURL = `${API_URL}/api/v1/article`;

  // 記事の取得（一覧）
  async getArticles(params?: ArticleSearchParams): Promise<{
    articles: Article[];
    total: number;
    page: number;
    limit: number;
  }> {
    const searchParams = new URLSearchParams();

    if (params?.keyword) searchParams.append('keyword', params.keyword);
    if (params?.categories?.length)
      searchParams.append('categories', params.categories.join(','));
    if (params?.tags?.length)
      searchParams.append('tags', params.tags.join(','));
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.sortBy) searchParams.append('sort_by', params.sortBy);
    if (params?.sortOrder) searchParams.append('sort_order', params.sortOrder);

    const response = await fetch(
      `${this.baseURL}/all?${searchParams.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error('記事の取得に失敗しました');
    }

    const data = await response.json();
    return {
      articles: data.articles || [],
      total: data.total || 0,
      page: data.page || 1,
      limit: data.limit || 10,
    };
  }

  // 記事の取得（個別）
  async getArticle(id: number): Promise<Article> {
    const response = await fetch(`${this.baseURL}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('記事の取得に失敗しました');
    }

    return await response.json();
  }

  // 記事の作成
  async createArticle(formData: ArticleFormData): Promise<Article> {
    const body = new FormData();

    // JSON データを文字列化して追加
    const articleData = {
      title: formData.title,
      content: formData.content,
      summary: formData.summary,
      categories: formData.categories,
      tags: formData.tags,
      authorId: formData.authorId,
      authorName: formData.authorName,
    };

    body.append('data', JSON.stringify(articleData));

    // アイキャッチ画像がある場合は追加
    if (formData.featuredImage instanceof File) {
      body.append('featured_image', formData.featuredImage);
    }

    const response = await fetch(`${this.baseURL}/create`, {
      method: 'POST',
      body,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('記事の作成に失敗しました');
    }

    return await response.json();
  }

  // 記事の更新
  async updateArticle(id: number, formData: ArticleFormData): Promise<Article> {
    const body = new FormData();

    const articleData = {
      title: formData.title,
      content: formData.content,
      summary: formData.summary,
      categories: formData.categories,
      tags: formData.tags,
      authorId: formData.authorId,
      authorName: formData.authorName,
    };

    body.append('data', JSON.stringify(articleData));

    if (formData.featuredImage instanceof File) {
      body.append('featured_image', formData.featuredImage);
    }

    const response = await fetch(`${this.baseURL}/${id}/update`, {
      method: 'PUT',
      body,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('記事の更新に失敗しました');
    }

    return await response.json();
  }

  // 記事の削除
  async deleteArticle(id: number): Promise<void> {
    const response = await fetch(`${this.baseURL}/${id}/delete`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('記事の削除に失敗しました');
    }
  }

  // カテゴリの取得
  async getCategories(): Promise<ArticleCategory[]> {
    const response = await fetch(`${API_URL}/api/v1/category/all`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('カテゴリの取得に失敗しました');
    }

    return await response.json();
  }

  // カテゴリの作成
  async createCategory(
    category: Omit<ArticleCategory, 'id'>
  ): Promise<ArticleCategory> {
    const response = await fetch(`${API_URL}/api/v1/category/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(category),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('カテゴリの作成に失敗しました');
    }

    return await response.json();
  }

  // カテゴリの更新
  async updateCategory(
    id: number,
    category: Partial<ArticleCategory>
  ): Promise<ArticleCategory> {
    const response = await fetch(`${API_URL}/api/v1/category/${id}/update`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(category),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('カテゴリの更新に失敗しました');
    }

    return await response.json();
  }

  // カテゴリの削除
  async deleteCategory(id: number): Promise<void> {
    const response = await fetch(`${API_URL}/api/v1/category/${id}/delete`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('カテゴリの削除に失敗しました');
    }
  }

  // タグの取得
  async getTags(): Promise<ArticleTag[]> {
    const response = await fetch(`${API_URL}/api/v1/tag/all`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('タグの取得に失敗しました');
    }

    return await response.json();
  }

  // タグの作成
  async createTag(tag: Omit<ArticleTag, 'id' | 'count'>): Promise<ArticleTag> {
    const response = await fetch(`${API_URL}/api/v1/tag/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(tag),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('タグの作成に失敗しました');
    }

    return await response.json();
  }

  // タグの削除
  async deleteTag(id: number): Promise<void> {
    const response = await fetch(`${API_URL}/api/v1/tag/${id}/delete`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('タグの削除に失敗しました');
    }
  }

  // 画像アップロード（TinyMCE用）
  async uploadImage(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_URL}/api/v1/image/upload_for_tinymce`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('画像のアップロードに失敗しました');
    }

    return await response.json();
  }
}

const articleService = new ArticleServiceImpl();
export default articleService;
